import { useAppState } from "../../../../Stores/AppState";
import { useConversation } from "../../../../Stores/Conversation";
import { useInvitation } from "../../../../Stores/Invitation";
import { useUserStore } from "../../../../Stores/User";
import {
    getConversationMembers,
    getLiveConversationParticipantIds,
} from "./useNewMembershipData";
import { syncRoleProfilesConversationParticipation } from "./workPanelMembershipAgentParticipation";
import {
    resolveUnstartedWorkPanelConversationParticipantIds,
    shouldPreserveDefaultParticipationForUnstartedWorkPanel,
} from "./workPanelMembershipUnstartedConversationParticipation";
import { canRestoreQuickConversationMembershipDraftForConversation } from "./workPanelMembershipQuickConversationSelection";
import { rememberActiveConversationBeforeMembershipSwitch } from "./workPanelMembershipRememberConversation";
import { closeCompetingRightDrawerModules } from "../../../Common/Composables/closeSubpanelAndOpenConversation";

export { rememberActiveConversationBeforeMembershipSwitch };

function isEmptyQuickConversationCandidate(conversation) {
    return (
        !conversation?.hasTwilioConversationSet?.() &&
        !conversation?.getMessages?.()?.length &&
        !conversation?.is_dm &&
        !conversation?.is_group &&
        !conversation?.is_topic
    );
}

/**
 * Whether Membership preload may re-apply a Quick Conversation draft.
 * Requires an exact cache-key match to the active resource so a previous
 * item's selection cannot paint onto a newly opened work-panel entity.
 *
 * @param {object|null|undefined} conversation
 * @param {{
 *   has_fetched_members_with_role_from_quick_conversation?: boolean,
 *   quick_conversation_cache_key?: string|null,
 *   has_conversation?: boolean,
 *   RESOURCE_TYPES?: Record<string, string>,
 * }} invitationStore
 * @returns {boolean}
 */
export function shouldApplyQuickConversationMembershipState(
    conversation,
    invitationStore,
) {
    if (
        !canRestoreQuickConversationMembershipDraftForConversation({
            hasFetched:
                invitationStore.has_fetched_members_with_role_from_quick_conversation,
            storedCacheKey: invitationStore.quick_conversation_cache_key,
            resourceTypes: invitationStore.RESOURCE_TYPES,
            conversation,
        })
    ) {
        return false;
    }

    // Never overlay the unstarted QC select-all draft onto an initiated
    // conversation's real participant list.
    if (invitationStore.has_conversation) {
        return false;
    }

    if (!isEmptyQuickConversationCandidate(conversation)) {
        return false;
    }

    const liveParticipants = getConversationMembers(conversation);
    const currentUserId = useUserStore().getCurrentUserProfileId();
    const isCurrentUserParticipant = liveParticipants.some(
        (participant) => Number(participant?.id) === Number(currentUserId),
    );

    if (
        liveParticipants.length > 0 &&
        !isCurrentUserParticipant &&
        !conversation?.is_dm &&
        !conversation?.is_group &&
        !conversation?.is_topic
    ) {
        return false;
    }

    return true;
}

export function reconcileWorkPanelMembershipParticipationAfterPreload() {
    const conversationStore = useConversation();
    const invitationStore = useInvitation();
    const userStore = useUserStore();
    const conversation = conversationStore.active;

    if (!conversation) {
        return;
    }

    if (shouldApplyQuickConversationMembershipState(conversation, invitationStore)) {
        const currentUserId = userStore.getCurrentUserProfileId();

        if (currentUserId) {
            invitationStore.addSelectedUserToQuickConversation(currentUserId);
        }

        invitationStore.setQuickConversationCheckedUsersAndAssigneeToUserProfilesWithRoles();

        return;
    }

    invitationStore.resetQuickConversation();

    if (
        !isWorkPanelEntityConversation(conversation) &&
        !isGroupOrTopicConversation(conversation)
    ) {
        return;
    }

    if (invitationStore.has_conversation) {
        return;
    }

    // Unstarted work-panel conversations already default everyone to
    // is_conversation_participant=true in fetchMembersWithRole (same default
    // Quick Conversation uses for select-all). Do not overwrite that from an
    // empty live participant list.
    if (
        shouldPreserveDefaultParticipationForUnstartedWorkPanel(
            invitationStore.has_conversation,
            isWorkPanelEntityConversation(conversation),
        )
    ) {
        invitationStore.current_participants =
            resolveUnstartedWorkPanelConversationParticipantIds(
                invitationStore.user_profiles_with_roles,
            );

        return;
    }

    const liveParticipantIds = getLiveConversationParticipantIds(conversation);

    syncRoleProfilesConversationParticipation({
        roleProfiles: invitationStore.user_profiles_with_roles,
        liveParticipantIds,
        updateParticipantAccess: (profileId, isParticipant) =>
            invitationStore.updateParticipantAccess(profileId, isParticipant),
    });

    invitationStore.current_participants =
        invitationStore.user_profiles_with_roles
            ?.filter((roleProfile) => roleProfile.is_conversation_participant)
            .map((roleProfile) => roleProfile.id) ?? [];
}

export function isWorkPanelEntityConversation(conversation) {
    if (!conversation) {
        return false;
    }

    return (
        conversation.is_space ||
        conversation.is_space_group ||
        conversation.is_board ||
        conversation.is_board_group ||
        conversation.is_item ||
        conversation.is_item_group ||
        (typeof conversation.isItem === "function" && conversation.isItem()) ||
        (typeof conversation.isBoard === "function" && conversation.isBoard()) ||
        (typeof conversation.isSpace === "function" && conversation.isSpace())
    );
}

export function isGroupOrTopicConversation(conversation) {
    if (!conversation) {
        return false;
    }

    return (
        conversation.is_group ||
        conversation.is_topic ||
        conversation.is_group_one_off ||
        (typeof conversation.isGroup === "function" && conversation.isGroup())
    );
}

export function setMembershipTabForConversation(appChat, conversation) {
    if (isGroupOrTopicConversation(conversation)) {
        appChat.setNewMemberTab();

        return;
    }

    if (isWorkPanelEntityConversation(conversation)) {
        appChat.setNewWorkPanelMemberTab();

        return;
    }

    appChat.setMemberTab();
}

export function isMembershipTabActiveForConversation(appChat, conversation) {
    if (isGroupOrTopicConversation(conversation)) {
        return appChat.is_new_member_tab;
    }

    if (isWorkPanelEntityConversation(conversation)) {
        return appChat.is_new_workpanel_member_tab;
    }

    return appChat.is_member_tab;
}

export function isMembershipDataPrefetchedForConversation(appChat, conversation) {
    return (
        conversation?.id != null &&
        appChat.membership_prefetched_conversation_id === conversation.id
    );
}

export function prepareMembershipPanelOpen() {
    const appStateStore = useAppState();
    const conversationStore = useConversation();

    appStateStore.toggleInviteDrawer(false);
    conversationStore.toggleProductivityPanel(false);
    closeCompetingRightDrawerModules();
}

export async function preloadMembershipData(requestConfig = {}) {
    const conversationStore = useConversation();
    const invitationStore = useInvitation();
    const appStateStore = useAppState();

    const activeConversation = conversationStore.active;

    if (!activeConversation) {
        return false;
    }

    const conversationIdAtStart = activeConversation.id;
    const isStalePreload = () =>
        conversationStore.active?.id !== conversationIdAtStart;

    const conversationType =
        activeConversation?.state?.type ?? activeConversation?.type;
    const resourceType = invitationStore.RESOURCE_TYPES[conversationType];
    const resourceId =
        activeConversation?.state?.type_id ?? activeConversation?.type_id;
    const isWorkPanelEntity =
        isWorkPanelEntityConversation(activeConversation);
    const shouldFetchMembersWithRole =
        !activeConversation?.is_group &&
        !activeConversation?.is_group_one_off &&
        Boolean(resourceType) &&
        Boolean(resourceId) &&
        (isWorkPanelEntity || !appStateStore.invite_drawer?.is_team_opened);

    const preloadStillValid = () => !isStalePreload();

    if (shouldFetchMembersWithRole) {
        const hasFetchedMembersWithRole =
            await invitationStore.fetchMembersWithRole(
                resourceType,
                resourceId,
                requestConfig,
                { isStillValid: preloadStillValid },
            );

        if (!hasFetchedMembersWithRole) {
            return false;
        }
    }

    const invitedProfilesLoaded = await invitationStore.invitedProfiles(
        null,
        requestConfig,
        { isStillValid: preloadStillValid },
    );

    if (!invitedProfilesLoaded) {
        return false;
    }

    if (isStalePreload()) {
        return false;
    }

    reconcileWorkPanelMembershipParticipationAfterPreload();

    return true;
}

export async function openMembershipTabForConversation(
    appChat,
    conversation,
    requestConfig = {},
) {
    const conversationStore = useConversation();
    const appStateStore = useAppState();
    const activeConversation = conversation ?? conversationStore.active;

    if (!activeConversation) {
        return false;
    }

    prepareMembershipPanelOpen();

    const usesNewMembershipPanel =
        isGroupOrTopicConversation(activeConversation) ||
        isWorkPanelEntityConversation(activeConversation);

    const previousChatTab = appChat.tab;
    const previousSelectedVariantTab = appStateStore.selected_variant_tab;
    const previousStoredVariantTab =
        appStateStore.previous_selected_variant_tab;

    setMembershipTabForConversation(appChat, activeConversation);

    if (usesNewMembershipPanel) {
        appStateStore.setPreviousSelectedVariantTab("members");
        appStateStore.setSelectedVariantTab("members");

        if (
            !isMembershipDataPrefetchedForConversation(
                appChat,
                activeConversation,
            )
        ) {
            appChat.setMembershipDataLoading(true);

            try {
                const isPreloaded = await preloadMembershipData(requestConfig);

                if (!isPreloaded) {
                    appChat.setTab(previousChatTab);
                    appStateStore.setSelectedVariantTab(
                        previousSelectedVariantTab,
                    );
                    appStateStore.setPreviousSelectedVariantTab(
                        previousStoredVariantTab,
                    );

                    return false;
                }

                appChat.setMembershipPrefetchedConversationId(
                    conversationStore.active?.id ?? null,
                );
            } finally {
                appChat.setMembershipDataLoading(false);
            }
        }
    }

    return true;
}

export function getWorkPanelMembershipPanelTitle(conversation) {
    if (!conversation) {
        return "Membership";
    }

    if (conversation.is_space_group) {
        return "Workspace Group Membership";
    }

    if (conversation.is_space) {
        return "Workspace Membership";
    }

    if (conversation.is_board_group) {
        return "Board Group Membership";
    }

    if (conversation.is_board) {
        return "Board Membership";
    }

    if (conversation.is_item_group) {
        return "Item Group Membership";
    }

    if (conversation.is_item) {
        return "Item Membership";
    }

    return "Membership";
}
